<template>
  <div class="form-item-container mb-6">
    <div class="fixed-content-container">
      <div class="header pb-2">
        <div class="header-title">
          <v-icon class="item-icon cursor-move" icon="mdi-drag-vertical" size="large"></v-icon>
          <div class="font-weight-black">{{ $t('i18n.navigationItemTitle') }}</div>
        </div>
        <div class="header-tool-icons">
          <v-btn class="cursor-pointer" variant="text" icon @click="duplicateItem" :disabled="!userCanEditFormItems">
            <v-icon icon="$gorateDuplicate"></v-icon>
            <v-tooltip activator="parent" location="start">
              {{ $t('i18n.duplicate') }}
            </v-tooltip>
          </v-btn>
          <v-btn class="cursor-pointer" variant="text" icon @click="removeItem" :disabled="!userCanEditFormItems">
            <v-icon size="small" icon="$gorateDeleteOutline"></v-icon>
            <v-tooltip activator="parent" location="start">
              {{ $t('i18n.remove') }}
            </v-tooltip>
          </v-btn>
          <v-btn class="cursor-pointer" variant="text" icon @click="expandGrid" :disabled="!userCanEditFormItems">
            <v-icon :icon="expandGridButtonIconName" size="large"></v-icon>
            <v-tooltip activator="parent" location="start">
              {{ $t('i18n.collapse') }}
            </v-tooltip>
          </v-btn>
        </div>
      </div>
    </div>
    <div ref="collapsableContentContainer" class="collapsable-content-container">
      <div style="overflow: hidden;">
        <div class="body pb-1">
          <div class="font-weight-black">{{ $t('i18n.language1') }}</div>
          <v-text-field v-model="itemData.question_lang1" density="compact" :placeholder="$t('i18n.questionLang1')"
            variant="outlined" dense hide-details="auto" clearable :rules="questionRules" counter
            :disabled="!userCanEditFormItems">
          </v-text-field>
          <v-tooltip v-if="!userCanEditFormItems" activator="parent" location="center">
            {{ $t('i18n.editNotAvailable') }}
          </v-tooltip>
        </div>
        <div v-if="bilingual" class="body pb-1">
          <div class="font-weight-black">{{ $t('i18n.language2') }}</div>
          <v-text-field v-model="itemData.question_lang2" density="compact" :placeholder="$t('i18n.questionLang2')"
            variant="outlined" dense hide-details="auto" clearable :rules="questionRules" counter></v-text-field>
        </div>
        <div class="form-refs-container">
          <v-list>
            <div class="font-weight-black pb-2">{{ $t('i18n.referencesToForms') }} <span
                v-if="itemData.form_item_options">({{ itemData.form_item_options.length }})</span></div>
            <v-list-item v-for="(option, index) in itemData.form_item_options" :key="index">
              <v-row class="pb-0">
                <v-col class="pb-0">
                  Título
                </v-col>
              </v-row>
              <v-row class="pt-0">
                <v-col class="pt-0">
                  <v-text-field v-model="option.text_lang1" counter
                    :placeholder="$t('i18n.navigationPlaceholderLang1', { index: index + 1 })" required
                    density="compact" variant="outlined" dense hide-details="auto" clearable :rules="optionRules"
                    :disabled="!userCanEditFormItems">
                  </v-text-field>
                  <v-tooltip v-if="!userCanEditFormItems" activator="parent" location="center">
                    {{ $t('i18n.editNotAvailable') }}
                  </v-tooltip>
                  <div>{{ $t('i18n.associatedForm') }}</div>
                  <v-select v-model="option.referenced_form_id" :items=formList
                    :placeholder="$t('i18n.formListPlaceholder')" variant="outlined" density="compact" required
                    item-title="name" item-value="id" :disabled="!userCanEditFormItems" :rules="referencedFormRules"
                    :no-data-text="$t('i18n.noForms')"></v-select>
                </v-col>
                <v-col v-if="bilingual" class="pt-0">
                  <div></div>
                  <v-text-field v-model="option.text_lang2" counter
                    :placeholder="$t('i18n.navigationPlaceholderLang2', { index: index + 1 })" required
                    density="compact" variant="outlined" dense hide-details="auto" clearable
                    :rules="optionRules"></v-text-field>
                </v-col>
                <v-col cols=auto class="pt-0">
                  <v-btn variant="flat" icon @click="removeFormReference(index)">
                    <v-icon size="small" icon="$gorateDeleteOutline" :disabled="!userCanEditFormItems"></v-icon>
                    <v-tooltip activator="parent" location="start">
                      {{ $t('i18n.remove') }}
                    </v-tooltip>
                  </v-btn>
                </v-col>
                <v-col cols=auto class="pt-0">
                  <v-btn variant="text" icon @click="moveUp(index)">
                    <v-icon size="x-small" icon="mdi-arrow-up"></v-icon>
                    <v-tooltip activator="parent" location="start">
                      {{ $t('i18n.moveUp') }}
                    </v-tooltip>
                  </v-btn>
                  <v-btn variant="text" icon @click="moveDown(index)">
                    <v-icon size="x-small" icon="mdi-arrow-down"></v-icon>
                    <v-tooltip activator="parent" location="start">
                      {{ $t('i18n.moveDown') }}
                    </v-tooltip>
                  </v-btn>
                </v-col>
              </v-row>

              <v-divider v-if="itemData.form_item_options && index < itemData.form_item_options.length - 1"
                :thickness="4"></v-divider>

            </v-list-item>
          </v-list>
        </div>
        <v-btn variant="outlined" @click="addFormReference" :disabled="!userCanEditFormItems">{{
          $t('i18n.addFormReference') }}
        </v-btn>
        <v-alert v-if="optionCountErrorMessage" class="mt-3 mb-3" variant="outlined" type="warning" dense closable>
          {{ optionCountErrorMessage }}
        </v-alert>
      </div>
    </div>
  </div>
  <v-divider :thickness="2" class="border-opacity-100 mb-3" color="grey"></v-divider>
</template>

<script lang="ts">
import { FormDeviceAssociation, FormItem, FormItemOption } from '@/utils/DataTypes'
import { defineComponent, PropType, ref } from 'vue'

export default defineComponent({
  name: 'NavigationItem',
  setup () {
    const collapsableContentContainer = ref<HTMLElement | null>(null)
    const isExpanded = ref(true)

    const expandGrid = () => {
      if (collapsableContentContainer.value) {
        isExpanded.value = !isExpanded.value
        collapsableContentContainer.value.style.gridTemplateRows = isExpanded.value ? '1fr' : '0fr'
      }
    }

    return {
      collapsableContentContainer,
      isExpanded,
      expandGrid
    }
  },
  emits: ['duplicate-item', 'remove-item', 'form-item-update'],
  props: {
    indexInTheSketchForm: {
      type: Number,
      required: true
    },
    bilingual: {
      type: Boolean,
      required: false,
      default: false
    },
    formList: {
      type: Array as PropType<FormDeviceAssociation[]>,
      required: true,
      default: () => []
    },
    formItems: {
      type: Array as PropType<FormItem[]>,
      required: false,
      default: null
    },
    userCanEditFormItems: {
      type: Boolean,
      required: true,
      default: true
    }
  },
  components: {
  },
  data () {
    return {
      formItemOption: null as FormItemOption | null,
      itemData: {
        name: 'NAVIGATION',
        item_type_id: 5,
        question_lang1: '',
        question_lang2: '',
        required: true,
        bilingual: this.bilingual,
        form_item_options: [] as FormItemOption[]
      } as FormItem,
      optionCountErrorMessage: this.$t('i18n.referenceCountErrorMessage')
    }
  },
  watch: {
    formItems: {
      handler () {
        if (this.formItems) {
          if (this.formItems[this.indexInTheSketchForm] !== undefined) {
            this.itemData = this.formItems[this.indexInTheSketchForm]
          }
        }
      },
      immediate: true
    },
    bilingual: {
      handler (newBilingual) {
        this.itemData.bilingual = newBilingual
        if (!newBilingual) {
          if (this.itemData.form_item_options) {
            this.itemData.question_lang2 = ''
            this.itemData.form_item_options.forEach(option => {
              delete option.text_lang2
            })
          }
        }
      },
      immediate: true
    },
    formItemOption: {
      handler (newItemOption) {
        if (!this.itemData.form_item_options) {
          this.itemData.form_item_options = []
        }
        if (newItemOption && !this.itemData.form_item_options.includes(newItemOption)) {
          this.itemData.form_item_options.push(newItemOption)
        }
      }
    },
    itemData: {
      handler (newItemData) {
        if (!this.itemData.form_item_options) {
          this.itemData.form_item_options = []
        }
        /* this.setFormReferenceNumberHintMessage() */
        this.$emit('form-item-update', { formItemIndex: this.indexInTheSketchForm, data: newItemData })
      },
      deep: true
    }
  },
  methods: {
    formatItemTypeTitle (input: string): string {
      const words = input.toLowerCase().split('_')
      const formatted = words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
      return formatted
    },

    duplicateItem () {
      this.$emit('duplicate-item', this.indexInTheSketchForm)
    },
    removeItem () {
      this.$emit('remove-item', this.indexInTheSketchForm)
    },

    addFormReference () {
      if (!this.itemData.form_item_options) {
        this.itemData.form_item_options = []
      }
      this.itemData.form_item_options.push({ text_lang1: '', text_lang2: '', referenced_form_id: null, option_order: this.itemData.form_item_options.length } as FormItemOption)
      this.setFormReferenceNumberHintMessage()
    },
    removeFormReference (index: number) {
      if (!this.itemData.form_item_options) {
        this.itemData.form_item_options = []
      }
      this.itemData.form_item_options.splice(index, 1)
      this.setFormReferenceNumberHintMessage()
    },
    moveUp (index: number) {
      if (!this.itemData.form_item_options || this.itemData.form_item_options.length < 2) return

      const isFirstOption = (index === 0)
      if (isFirstOption) {
        const item = this.itemData.form_item_options.shift()
        if (item !== undefined) {
          this.itemData.form_item_options.push(item)
        }
      } else {
        const temp = this.itemData.form_item_options[index - 1]
        this.itemData.form_item_options[index - 1] = this.itemData.form_item_options[index]
        this.itemData.form_item_options[index] = temp
      }

      this.updateFormItemOptionsOrder()
    },
    moveDown (index: number) {
      if (!this.itemData.form_item_options || this.itemData.form_item_options.length < 2) return

      const isLastOption = (index === this.itemData.form_item_options.length - 1)
      if (isLastOption) {
        const item = this.itemData.form_item_options.pop()
        if (item !== undefined) {
          this.itemData.form_item_options.unshift(item)
        }
      } else {
        const temp = this.itemData.form_item_options[index + 1]
        this.itemData.form_item_options[index + 1] = this.itemData.form_item_options[index]
        this.itemData.form_item_options[index] = temp
      }

      this.updateFormItemOptionsOrder()
    },
    updateFormItemOptionsOrder () {
      if (!this.itemData.form_item_options) return

      this.itemData.form_item_options.forEach((option, index) => {
        option.option_order = index
      })
    },

    setFormReferenceNumberHintMessage () {
      if (!this.itemData.form_item_options) {
        this.itemData.form_item_options = []
      }
      if (this.itemData.form_item_options.length === 0) {
        this.optionCountErrorMessage = this.$t('i18n.referenceCountErrorMessage')
      } else {
        this.optionCountErrorMessage = ''
      }
    }
  },
  computed: {
    questionRules (): ((value: string) => boolean | string)[] {
      return [
        (value: string) => {
          if (value) return true
          return this.$t('i18n.questionRequired')
        }, (value: string) => {
          if (value && value.length <= 180) return true
          return this.$t('i18n.questionMaxLength', { max: 180 })
        }, (value: string) => {
          if (value && value.length >= 3) return true
          return this.$t('i18n.questionMinLength')
        }
      ]
    },
    optionRules (): ((value: string) => boolean | string)[] {
      return [
        (value: string) => {
          if (value) return true
          return this.$t('i18n.optionRequired')
        }, (value: string) => {
          if (value && value.length <= 180) return true
          return this.$t('i18n.optionMaxLength', { max: 180 })
        }, (value: string) => {
          if (value && value.length >= 1) return true
          return this.$t('i18n.optionMinLength')
        }
      ]
    },
    referencedFormRules (): ((value: string) => boolean | string)[] {
      return [
        (value: string) => {
          if (value) return true
          return this.$t('i18n.optionRequired')
        }
      ]
    },
    expandGridButtonIconName (): string {
      if (this.isExpanded) return 'mdi-chevron-up'
      else return 'mdi-chevron-down'
    }
  }
})
</script>

<style lang="scss" scoped>
@import "../../styles/colors.scss";

.form-item-container {

  .fixed-content-container {
    .header {
      display: grid;
      grid-template-rows: auto;
      align-items: center;

      .header-title {
        display: grid;
        grid-template-columns: auto auto;
        justify-self: start;
        align-items: center;
      }

      .header-tool-icons {
        grid-column: 2;
        justify-self: end;
      }

    }
  }

  .collapsable-content-container {
    display: grid;
    grid-template-rows: 1fr;
    transition: grid-template-rows 300ms;

    .body {
      display: grid;
      grid-template-columns: 6.5em 1fr;
      align-items: center;
    }

    .form-refs-container {
      display: grid;
      grid-template-rows: auto 1fr;

      .form-refs-rows {
        display: grid;
        grid-template-rows: 1fr 1fr;

        .ref-title-container {
          display: grid;
          grid-template-columns: 1fr 1fr auto;

          .options-container {
            background-color: blue;
          }
        }

        .form-selection-container {}
      }
    }
  }

}
</style>
